import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalService } from 'ng-zorro-antd/modal';
import { filter } from 'rxjs';

import { CallComponent } from './components/call/call.component';
import { CallAudioService } from './components/call/services/call-audio.service';
import { NetworkDisconnectNoticeComponent } from './components/network-disconnect-notice/network-disconnect-notice.component';
import { replacePhoneNumber } from './constants/until';
import { environment } from './environments/environment';
import { ChatStore } from './pages/chat/chat.store';
import { LoginService } from './pages/login/services/login.service';
import { ModalPermissionComponent } from './pages/meeting-management/components/modal-permission/modal-permission.component';
import { BroadcastService } from './services/broadcast.service';
import { HomieService } from './services/homie.service';
import { LogoutService } from './services/logout.service';
import { LocalNotificationService } from './services/notification.service';
import { ThemeService } from './services/theme.service';
import { TokenRenewService } from './services/token-renew.service';
import { UserScopedStorageService } from './services/user-scoped-storage.service';
import { ZegoEngineService } from './services/zego-engine.service';
import { ZimService } from './services/zim.service';
import { getUserInfo } from './utils/utils';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    CallComponent,
    NetworkDisconnectNoticeComponent,
    ModalPermissionComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly notifyService = inject(LocalNotificationService);
  private readonly renderer = inject(Renderer2);
  private readonly callAudio = inject(CallAudioService);
  private readonly chatStore = inject(ChatStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zego = inject(ZegoEngineService);
  private readonly router = inject(Router);
  private readonly loginService = inject(LoginService);
  private readonly tokenRenewService = inject(TokenRenewService);
  private readonly zimService = inject(ZimService);
  private readonly broadcastService = inject(BroadcastService);
  private readonly logoutService = inject(LogoutService);
  private readonly themeService = inject(ThemeService);
  private readonly modalService = inject(NzModalService);
  private readonly userScopedStorage = inject(UserScopedStorageService);
  private readonly homieService = inject(HomieService);

  readonly soundEnabled = this.callAudio.soundEnabled;

  channel = new BroadcastChannel('user-refresh');

  private audioInitialized = false;

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.channel.postMessage({ type: 'REFRESH_USER', at: Date.now() });
  }

  @HostListener('document:click')
  @HostListener('document:touchstart')
  onFirstInteraction() {
    this.callAudio.enableSound();
  }

  @HostListener('document:keydown', ['$event'])
  handle(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.modalService.openModals.length > 0) return;
      this.chatStore.clearSelectedConversation();
    }
  }

  constructor() {
    // Apply persisted theme at app bootstrap.
    this.themeService.mode();
    this.zego.initEngine(environment.ZEGO_APP_ID, environment.ZEGO_SERVER_WS);
    this.zimService.createZIM();
    this.broadcastService.logout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.logoutService.markSelfLogout();
      this.zimService.logout();
      this.loginService.clearStorage();
      this.router.navigateByUrl('/login').then(() => {
        window.location.reload();
      });
    });
    this.broadcastService.login$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.router.navigateByUrl('/chat').then(() => {
        window.location.reload();
      });
    });
  }

  async ngOnInit() {
    // if (environment.ENV === 'production') {
    //   this.logSdkService.init();
    //   this.logSdkService.setEncodeEnabled(true);
    // } else {
    //   this.logSdkService.init();
    //   this.logSdkService.setEncodeEnabled(false);
    // }
    const isClonedTab = !!sessionStorage.getItem('isDuplicated');
    if (isClonedTab) {
      sessionStorage.removeItem('token_zego');
      sessionStorage.removeItem('token_zego_w');
    }
    sessionStorage.setItem('isDuplicated', '1');
    window.addEventListener('pagehide', () => {
      sessionStorage.removeItem('isDuplicated');
    });

    window.addEventListener('pagehide', () => {
      this.zimService.logout();
    });

    this.channel.onmessage = async (event) => {
      if (event.data?.type === 'REFRESH_USER') {
        await this.refreshUser();
      }
    };

    await this.refreshUser();
    this.channel.postMessage({ type: 'REFRESH_USER', at: Date.now() });
    this.homieService.loadStatus();

    if (this.notifyService.permissionStatus() === 'default') {
      const unlisten = this.renderer.listen('document', 'click', () => {
        console.log('User interacted! Requesting Native Permission...');
        this.notifyService.requestPermission();
        unlisten();
      });
    }

    if (isClonedTab && localStorage.getItem('token')) {
      await this.tokenRenewService.login();
    }
  }

  ngAfterViewInit() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async () => {
        const userInfo = getUserInfo();
        const token = localStorage.getItem('token');

        if (!userInfo || !token) {
          this.loginService.clearStorage();
          if (
            !this.router.url.startsWith('/login') &&
            !this.router.url.startsWith('/download-app') &&
            !this.router.url.startsWith('/openinapp')
          ) {
            this.router.navigate(['/login']);
          }
          return;
        }
        await this.tokenRenewService.login();
        const tokenZego = sessionStorage.getItem('token_zego');
        this.zimService.login(userInfo.zegoId, tokenZego || '');
      });
  }

  ngOnDestroy() {
    this.zego.destroyEngine();
  }

  enableAudioOnce() {
    if (this.audioInitialized) return;

    this.audioInitialized = true;
    this.callAudio.enableSound();
  }

  private async refreshUser() {
    const token = localStorage.getItem('token');
    if (!token) return;
    this.loginService
      .userInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const currentUserInfo = getUserInfo();
          if (result?.success && result.data) {
            const userInfo = {
              ...result.data,
              phoneNumber: replacePhoneNumber(result.data?.phoneNumber),
              imageUrl: result.data?.imageUrl || currentUserInfo.imageUrl,
              nickname: result.data?.nickname || currentUserInfo.nickname,
            };
            localStorage.setItem('user_info', JSON.stringify(userInfo));
            this.userScopedStorage.syncUser();
            this.chatStore.userInfo.set(getUserInfo());

            this.zimService.syncPrivacySettings();
          }
        },
        error: (err) => {
          console.error('errrr login', err);
        },
      });
  }

  enableCallAudio() {
    this.callAudio.markUserEnabled();
  }
}
